const DB_NAME = 'trri_geojson_cache';
const STORE_NAME = 'geojson_store';
const CACHE_VERSION = '1.0';
const CACHE_DURATION = 7 * 24 * 60 * 60 * 1000;

interface CachedGeoJSON {
  url: string;
  data: GeoJSON.FeatureCollection;
  timestamp: number;
  version: string;
}

class IndexedDBCache {
  private dbPromise: Promise<IDBDatabase> | null = null;

  private openDB(): Promise<IDBDatabase> {
    if (this.dbPromise) {
      return this.dbPromise;
    }

    this.dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, 1);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const objectStore = db.createObjectStore(STORE_NAME, {
            keyPath: 'url',
          });
          objectStore.createIndex('timestamp', 'timestamp', { unique: false });
          objectStore.createIndex('version', 'version', { unique: false });
        }
      };
    });

    return this.dbPromise;
  }

  async get(url: string): Promise<CachedGeoJSON | null> {
    try {
      const db = await this.openDB();

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.get(url);

        request.onsuccess = () => {
          const result = request.result as CachedGeoJSON | undefined;

          if (!result) {
            resolve(null);
            return;
          }

          const isExpired = Date.now() - result.timestamp > CACHE_DURATION;
          const isVersionMismatch = result.version !== CACHE_VERSION;

          if (isExpired || isVersionMismatch) {
            this.delete(url).catch((error) => {
              console.warn('[GeoJSON Cache] Failed to delete expired cache:', error);
            });
            resolve(null);
          } else {
            resolve(result);
          }
        };

        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.warn('[GeoJSON Cache] Error reading from IndexedDB:', error);
      return null;
    }
  }

  async set(cacheData: CachedGeoJSON): Promise<void> {
    try {
      const db = await this.openDB();

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.put(cacheData);

        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.warn('[GeoJSON Cache] Error writing to IndexedDB:', error);
    }
  }

  async delete(url: string): Promise<void> {
    try {
      const db = await this.openDB();

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.delete(url);

        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.warn('[GeoJSON Cache] Error deleting from IndexedDB:', error);
    }
  }
}

const cacheInstance = new IndexedDBCache();

export const getCachedGeoJSON = async (url: string): Promise<GeoJSON.FeatureCollection> => {
  try {
    const cached = await cacheInstance.get(url);

    if (cached) {
      return cached.data;
    }
  } catch (error) {
    console.warn('[GeoJSON Cache] Error reading from cache:', error);
  }

  const response = await fetch(url);
  const data = await response.json();

  try {
    const cacheData: CachedGeoJSON = {
      url,
      data,
      timestamp: Date.now(),
      version: CACHE_VERSION,
    };

    await cacheInstance.set(cacheData);
  } catch (error) {
    console.warn('[GeoJSON Cache] Error writing to cache:', error);
  }

  return data;
};
